import FilterIcon from '@precis-digital/kurama/icons/FilterIcon'
import { styled } from '@precis-digital/kurama/utils'
import { TableBody, TableRow } from '@precis-digital/kurama/Table'
import Typography from '@precis-digital/kurama/Typography'
import ThemedImage from 'shared/components/Themed/Image'
import { CHART_CARD_TABLE_LAYOUT } from '../../constants'
import type { ChartCardDefaults } from '../../types'
import type { CellAlignment } from './types'

export const StyledCellContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'gap' && prop !== 'justifyContent' && prop !== 'width'
})(
  ({
    gap,
    justifyContent,
    width
  }: {
    gap?: string
    justifyContent?: 'start' | 'end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
    width?: string | number
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent,
    flex: 1,
    minWidth: CHART_CARD_TABLE_LAYOUT.defaultColumn.minSize,
    maxWidth: CHART_CARD_TABLE_LAYOUT.defaultColumn.maxSize,
    overflow: 'hidden',
    gap,
    width
  })
)

export const StyledPaddedDecorationContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  padding: 0
})

export const StyledHeaderLabel = styled(Typography, { shouldForwardProp: (prop) => prop !== 'textAlign' })<{
  textAlign: CellAlignment
}>(({ theme, textAlign }) => ({
  textAlign,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.tokens.foreground.main.primary,
  ...theme.typography.h5
}))

export const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'isInteractionDisabled'
})(({ height, isInteractionDisabled }: { height?: number; isInteractionDisabled?: boolean }) => ({
  display: 'grid',
  order: 2,
  position: 'relative',
  // Fill the flexible middle row when the table is taller than its rows (short last page).
  minHeight: typeof height === 'number' && height > 0 ? height : undefined,
  height: '100%',
  alignContent: 'start',
  ...(isInteractionDisabled === true && { pointerEvents: 'none' })
}))

export const StyledTableBodyRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'rowTop' && prop !== 'hovered' && prop !== 'defaults'
})<{ hovered: boolean; defaults: ChartCardDefaults }>(({ hovered, defaults }) => ({
  display: 'flex',
  width: '100%',
  backgroundColor: hovered
    ? defaults.table.hoverBackgroundColor.body ?? defaults.table.backgroundColor.body
    : defaults.table.backgroundColor.body
}))

export const StyledTableVirtualBodyRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'rowTop' && prop !== 'hovered' && prop !== 'defaults'
})<{ rowTop: number; hovered: boolean; defaults: ChartCardDefaults }>(({ rowTop, hovered, defaults }) => ({
  display: 'flex',
  position: 'absolute',
  top: rowTop,
  left: 0,
  width: '100%',
  backgroundColor: hovered
    ? defaults.table.hoverBackgroundColor.body ?? defaults.table.backgroundColor.body
    : defaults.table.backgroundColor.body
}))

export const StyledBodyText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textAlign' && prop !== 'variant' && prop !== 'width'
})<{ textAlign: CellAlignment; variant: 'h4' | 'body2' }>(({ theme, textAlign, variant }) => ({
  width: '100%',
  textAlign,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.tokens.foreground.main.primary,
  variant,
  ...theme.typography[variant]
}))

export const StyledAssetCellImage = styled(ThemedImage)(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: theme.spacing(0)
}))

export const ActiveFilterIcon = styled(FilterIcon)(({ theme }) => ({
  color: theme.tokens.foreground.main.link
}))

export const StyledFooterValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textAlign' && prop !== 'variant'
})<{ textAlign?: CellAlignment; variant?: 'h4' | 'body2' }>(({ theme, textAlign = 'left', variant = 'h4' }) => ({
  textAlign,
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.tokens.foreground.main.primary,
  ...theme.typography[variant]
}))

export const Spacer = styled('span', { shouldForwardProp: (prop) => prop !== 'width' })(
  ({ width }: { width: string | number }) => ({
    width
  })
)

export const StyledImage = styled(ThemedImage)({
  objectFit: 'contain',
  padding: 0
})
